import { Alert, Card, CardContent, CardHeader, ScrollArea } from '@ibsys/kit';
import { FC } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type KmEntity, useKmDictionaries } from '@/entities/ontology';
import { getApiErrorMessage } from '@/shared/api';
import { cn, formatTimeDate, SCROLL_AREA_VIEWPORT_BLOCK } from '@/shared/lib';
import { SaveButton } from '@/shared/ui';
import { KmEntityAttrsSection } from './KmEntityAttrsSection';
import { KmEntityCardTitle } from './KmEntityCardTitle';
import { KmEntityField } from './KmEntityField';
import { KmEntityPredicateField } from './KmEntityPredicateField';
import { KmEntityRelationsSection } from './KmEntityRelationsSection';
import { useKmEntityCardForm } from './useKmEntityCardForm';

type Props = {
  entity: KmEntity;
  /** Доступно ли изменение карточки (право на администрирование модели знаний) */
  canEdit: boolean;
};

/**
 * Загруженная карточка сущности: блоки правятся одной формой и сохраняются
 * единой кнопкой в её шапке.
 */
export const KmEntityCardView: FC<Props> = ({ entity, canEdit }) => {
  const { t } = useTranslation(['kmEntities', 'common']);
  const { getEntityTypeName, getDomainName } = useKmDictionaries();

  const { form, schemaRows, extraRows, resolveAttrType, onSubmit, isSaving, isDirty, saveError } =
    useKmEntityCardForm(entity);

  const typeName = getEntityTypeName(entity.entityTypeCode);
  const domainName = entity.domainCode ? getDomainName(entity.domainCode) : t('kmEntities:withoutDomain');

  return (
    <FormProvider {...form}>
      {/* Без блочного вьюпорта область прокрутки растягивается по ширине колонок гридов секций */}
      <ScrollArea className={cn('h-full', SCROLL_AREA_VIEWPORT_BLOCK)}>
        <div className="flex flex-col gap-4">
          <Card>
            <CardHeader className="border-b flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <KmEntityCardTitle entity={entity} canEdit={canEdit} />
              {canEdit && (
                <SaveButton
                  className="self-end sm:self-auto"
                  isDirty={isDirty}
                  isLoading={isSaving}
                  onClick={onSubmit}
                />
              )}
            </CardHeader>
            <CardContent className="flex flex-col gap-4 p-2 md:p-6">
              {!!saveError && <Alert type="error" title={getApiErrorMessage(saveError)} />}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <KmEntityField label={t('kmEntities:entityType')}>{typeName}</KmEntityField>
                <KmEntityField label={t('kmEntities:domain')}>{domainName}</KmEntityField>
                <KmEntityField label={t('kmEntities:createdAt')}>{formatTimeDate(entity.createdAt)}</KmEntityField>
                <KmEntityField label={t('kmEntities:updatedAt')}>{formatTimeDate(entity.updatedAt)}</KmEntityField>
                <KmEntityPredicateField canEdit={canEdit} />
              </div>
            </CardContent>
          </Card>

          <KmEntityAttrsSection
            schemaRows={schemaRows}
            extraRows={extraRows}
            resolveAttrType={resolveAttrType}
            canEdit={canEdit}
          />
          <KmEntityRelationsSection entityId={entity.id} />
        </div>
      </ScrollArea>
    </FormProvider>
  );
};
